import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { Container } from "@egalregal/shared";
import { effectiveSearchText, pathLabelOf } from "@egalregal/shared";
import { useAppStore } from "../../shared/store/app";
import { containerVectors, getMirroredItems, itemVectors } from "./catalogStore";
import { containerSearchableText, itemSearchableText } from "./searchableText";
import { hasKeywordDoc } from "./textFallback";

/**
 * Dev-gated searchable-text inspector (ADR-0033): per container/item it shows
 * the exact `fields` rows, the joined `text` both engines consume verbatim,
 * the char count, and which engine(s) actually index the entry. Read-only —
 * no editing, no re-indexing, and it reads state synchronously only: it never
 * touches `embeddingService` (in particular not the lazily-loading `ready`
 * getter), so opening it can never start a model load nor block a
 * pure-discrete query. Labels are plain literals on purpose: the component is
 * stripped from production bundles (see `searchDebugEnabled`), so i18n keys
 * would only add churn.
 */

/**
 * Gate for the inspector: on only with `?debug=search` AND a dev build.
 * `isDev` is `import.meta.env.DEV`, which Vite statically replaces, so a
 * production build drops the whole branch — and with it this component — while
 * no nav entry, menu item or button ever links to the view (ADR-0033).
 *
 * @param params current query, as a string (leading `?` tolerated) or parsed.
 * @param isDev whether the bundle is a development build.
 * @returns true when the inspector may render.
 */
export function searchDebugEnabled(
  params: URLSearchParams | string,
  isDev: boolean,
): boolean {
  if (!isDev) return false;
  const query = typeof params === "string" ? new URLSearchParams(params) : params;
  return query.get("debug") === "search";
}

/** Engines currently holding the entry's document, as rendered in the chip. */
export type IndexedBy = "keyword" | "semantic" | "both" | "none";

interface TextEntry {
  id: string;
  name: string;
  /** Disambiguation line: the container path, or the item category. */
  context: string;
  kind: "container" | "item";
  fields: Record<string, string>;
  text: string;
  indexedBy: IndexedBy;
}

export function SearchTextView() {
  const containers = useAppStore((s) => s.containers);
  const [filter, setFilter] = useState("");
  const entries = useMemo(() => buildEntries(containers), [containers]);
  const needle = filter.trim().toLowerCase();
  const visible = needle
    ? entries.filter((e) => e.name.toLowerCase().includes(needle))
    : entries;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Searchable text (dev)
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        fields → text → char count → indexed-by. Read-only: no re-index, no model load.
      </Typography>
      <TextField
        size="small"
        fullWidth
        label="Filter by name"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        sx={{ mb: 1 }}
      />
      {visible.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          No entries match.
        </Typography>
      ) : (
        visible.map((entry) => (
          <Box key={entry.id} data-testid={`searchtext-${entry.id}`} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" component="h3">
                {entry.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {entry.context}
              </Typography>
              <Chip label={entry.kind} size="small" />
              <Chip label={entry.indexedBy} size="small" />
            </Stack>
            <Table size="small" sx={{ mb: 0.5 }}>
              <TableBody>
                {Object.entries(entry.fields).map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ fontWeight: 600, whiteSpace: "nowrap", width: "1%" }}
                    >
                      {key}
                    </TableCell>
                    <TableCell>{value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {entry.text}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {`${entry.text.length} chars`}
            </Typography>
          </Box>
        ))
      )}
    </Box>
  );
}

/**
 * Truthful engine membership for one entry: keyword from the real Orama doc
 * set (`hasKeywordDoc`), semantic from the in-memory vector mirrors — reported
 * as observed, never assumed from what "should" be indexed.
 */
function indexedBy(id: string, vectors: Map<string, Float32Array>): IndexedBy {
  const keyword = hasKeywordDoc(id);
  const semantic = vectors.has(id);
  if (keyword && semantic) return "both";
  if (keyword) return "keyword";
  if (semantic) return "semantic";
  return "none";
}

/** One entry per non-deleted container and per mirrored item, in that order. */
function buildEntries(containers: Container[]): TextEntry[] {
  const containerEntries: TextEntry[] = containers
    .filter((c) => !c.deleted)
    .map((c) => {
      const path = pathLabelOf(containers, c.id);
      const { fields, text: liveText } = containerSearchableText(c, path);
      return {
        id: c.id,
        name: c.name,
        context: path,
        kind: "container" as const,
        fields,
        text: effectiveSearchText(c) ?? liveText,
        indexedBy: indexedBy(c.id, containerVectors),
      };
    });
  const itemEntries: TextEntry[] = getMirroredItems().map((item) => {
    const { fields, text: liveText } = itemSearchableText(item);
    return {
      id: item.id,
      name: item.name,
      context: item.category,
      kind: "item" as const,
      fields,
      text: effectiveSearchText(item) ?? liveText,
      indexedBy: indexedBy(item.id, itemVectors),
    };
  });
  return [...containerEntries, ...itemEntries];
}
